import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, ArrowLeft, GoogleLogo, Phone } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AdminForgotPassword = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [adminEmailHint, setAdminEmailHint] = useState('');

  const handleContinue = async (e) => {
    e.preventDefault();
    setError('');
    if (!phone.trim()) {
      setError('Please enter your admin phone number.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/request-password-reset`, {
        phone: phone.trim(),
      });

      if (!data.temp_token) {
        // Backend returns generic message when admin doesn't exist — don't leak
        setError('If that admin account exists, we would proceed with Google verification. Please double-check your phone number.');
        setLoading(false);
        return;
      }

      // Persist reset context before redirecting to Google
      sessionStorage.setItem('admin_pwreset_temp_token', data.temp_token);
      sessionStorage.setItem('admin_pwreset_phone', phone.trim());
      setAdminEmailHint(data.admin_email || '');

      const redirectUrl = window.location.origin + '/admin-password-reset-callback';
      window.location.href =
        `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start password reset. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center p-4"
      style={{ position: 'fixed', inset: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}
    >
      <div className="bg-wa-panel rounded-2xl shadow-2xl p-8 w-full max-w-md my-8">
        <button
          type="button"
          onClick={() => navigate('/admin-login')}
          className="flex items-center gap-1 text-sm text-wa-text-secondary hover:text-wa-text-primary mb-6"
          data-testid="admin-forgot-back-btn"
        >
          <ArrowLeft size={16} weight="bold" />
          Back to login
        </button>

        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-wa-hover flex items-center justify-center mb-4">
            <ShieldCheck size={40} weight="fill" className="text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-wa-text-primary">Reset Admin Password</h1>
          <p className="text-sm text-wa-text-secondary mt-2">
            We'll verify your identity with Google before letting you set a new password.
          </p>
        </div>

        <form onSubmit={handleContinue} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-2">
              Admin Phone Number
            </label>
            <div className="relative">
              <Phone size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-wa-text-secondary" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter admin phone number"
                className="w-full pl-10 pr-4 py-3 border border-wa-border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="admin-forgot-phone-input"
                required
              />
            </div>
          </div>

          {error && (
            <div className="bg-wa-hover border border-red-200 text-wa-text-primary px-4 py-3 rounded-lg text-sm" data-testid="admin-forgot-error">
              {error}
            </div>
          )}

          {adminEmailHint && (
            <div className="bg-wa-hover border border-purple-200 text-wa-text-primary px-3 py-2 rounded-lg text-xs">
              You'll be asked to sign in as <strong>{adminEmailHint}</strong> on the next screen.
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="admin-forgot-verify-btn"
          >
            <GoogleLogo size={20} weight="bold" />
            {loading ? 'Redirecting to Google...' : 'Verify with Google'}
          </button>

          <p className="text-xs text-wa-text-secondary text-center">
            Only the authorized admin Gmail can reset this password.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AdminForgotPassword;
